import React, { createContext, useContext, useState, useEffect } from 'react';

const SUPPORTED_LANGS = ['en', 'es', 'fr', 'zh', 'hi', 'pt-BR'];
const FALLBACK_LANG = 'en';

function detectBrowserLang() {
  try {
    const raw = navigator.language || navigator.userLanguage || '';
    if (SUPPORTED_LANGS.includes(raw)) return raw;
    const short = raw.split('-')[0];
    if (SUPPORTED_LANGS.includes(short)) return short;
    if (short === 'pt') return 'pt-BR';
    return FALLBACK_LANG;
  } catch {
    return FALLBACK_LANG;
  }
}

const translations = {
  en: {
    'app.name': 'PlaniAgent',
    'app.tagline': 'Your travel planning agent',
    'nav.chat': 'Chat',
    'nav.exploration': 'Exploration',
    'nav.itineraries': 'Itineraries',
    'nav.bookings': 'Bookings',
    'nav.saved': 'Saved',
    'nav.buyCoffee': 'Buy me a coffee',
    'nav.contact': 'Contact',
    'trip.current': 'Current Trip',
    'trip.edit': 'Edit',
    'trip.flights': 'Flights',
    'trip.hotels': 'Hotels',
    'trip.pending': 'Pending',
    'trip.oneSelected': '1 Selected',
    'trip.draftLabel': 'DRAFT ITINERARY',
    'trip.documents': 'Trip Documents',
    'trip.uploadPassport': 'Upload Passport',
    'contact.title': 'Contact',
    'contact.namePlaceholder': 'Your name',
    'contact.emailPlaceholder': 'Your email',
    'contact.messagePlaceholder': 'Questions, suggestions, or feedback...',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.success': 'Thank you! Your message has been sent.',
    'contact.error': 'Something went wrong. Please try again.',
    'chat.placeholder': 'Enter your travel request...',
    'chat.today': 'Today',
    'chat.empty': 'No messages yet. Start a conversation!',
    'chat.devChatId': 'Enter chat ID...',
    'hotel.intro': 'Here are some hotel options for you:',
    'hotel.select': 'Select This Hotel',
    'hotel.contactForPricing': 'Contact for pricing',
    'hotel.locationNotSpecified': 'Location not specified',
    'hotel.more': '+{n} more',
    'hotel.perNight': '/night',
    'hotel.fallbackName': 'Hotel',
    'hotel.bookingPrefix': "I'd like to book:",
    'hotel.fallbackBookingName': 'this hotel',
    'hotel.currencySymbol': '$',
    'trip.sampleDates': 'June 12-15',
    'trip.sampleLocation': 'Amalfi Coast, Italy',
    'trip.draftHotelName': 'Hotel Santa Caterina',
    'trip.draftHotelLocation': 'Positano',
    'trip.draftRestaurantName': 'La Sponda Dinner',
    'trip.draftRestaurantDesc': 'Michelin-starred',
    'item.activity': 'Activity',
    'item.details': 'Details',
    'item.googleMaps': 'Google Maps',
    'item.ticket': 'Ticket',
    'item.bookOnGetYourGuide': 'Book on GetYourGuide',
    'item.day': 'Day',
    'item.recommendations': 'Recommendations',
    'share.exportPdf': 'Export as PDF',
    'share.pdfTitle': 'PlaniAgent Itinerary',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': 'This itinerary was generated at {website}',
    'share.openInMaps': 'Open in Google Maps',
    'share.shareItinerary': 'Share Itinerary',
    'share.linkCopied': 'Link copied',
    'share.sharedItineraryMessage': 'This is a shared itinerary',
    'share.sharedItineraryTitle': 'Shared Itinerary',
    'share.loadingItinerary': 'Loading itinerary...',
    'nav.roteiro': 'Itinerary',
    'modal.fullScreen': 'Full screen',
    'error.api': 'Failed to fetch data from API: {message}',
    'bug.title': 'Report Bug',
    'bug.selectCategory': 'Select category',
    'bug.categoryUI': 'UI Issue',
    'bug.categoryFunctionality': 'Functionality Issue',
    'bug.categoryPerformance': 'Performance Issue',
    'bug.categoryContent': 'Content Issue',
    'bug.categoryOther': 'Other',
    'bug.descriptionPlaceholder': 'Describe the bug you encountered...',
    'bug.payloadLabel': 'API Request/Response Payload',
    'bug.submit': 'Submit Bug Report',
    'bug.sending': 'Sending...',
    'bug.success': 'Thank you! Your bug report has been sent.',
    'bug.error': 'Something went wrong. Please try again.',
    'bug.reportBug': 'Report Bug',
    'greeting': "Hello! I'm your personal travel agent. I can help with ~~flights~~, ~~hotels~~, or itineraries and recommendations. Where are we going today?",
    'defaultMessage': 'I want a complete 4-day daily itinerary for a couple in Rio de Janeiro departing from Dublin at a moderate pace, in a package travel with a mix of different activities, arriving at 2pm and departing at 6pm on the last day',
    'exploration.badge': 'Featured Itineraries',
    'exploration.title': 'Explore Itineraries Around the World',
    'exploration.subtitle': 'Select a curated itinerary to start planning directly with your AI travel agent.',
    'exploration.startPlanning': 'Plan This Itinerary',
    'exploration.filterRegion': 'Region',
    'exploration.filterDuration': 'Duration',
    'exploration.filterTravelStyle': 'Travel Style',
    'exploration.filterAll': 'All',
    'exploration.filterClear': 'Clear Filters',
    'exploration.regionEurope': 'Europe',
    'exploration.regionBrazil': 'Brazil',
    'exploration.regionAmericas': 'Americas',
    'exploration.regionAsia': 'Asia',
    'exploration.regionAfrica': 'Africa',
    'exploration.styleRomantic': 'Romantic',
    'exploration.styleHistoric': 'Historic',
    'exploration.styleBeach': 'Beach',
    'exploration.styleGastronomic': 'Gastronomic',
    'exploration.styleUrban': 'Urban',
    'exploration.styleAdventure': 'Adventure',
    'exploration.styleCultural': 'Cultural',
    'exploration.styleNature': 'Nature',
    'exploration.duration4Days': '4 days',
    'exploration.duration5Days': '5 days',
    'exploration.durationDays': '{days} days',
    'exploration.durationCustom': 'Custom',
    'exploration.loadingItinerary': 'Loading itinerary...',
    'exploration.loadingConnecting': 'Connecting to agent...',
    'exploration.loadingRequesting': 'Requesting itinerary...',
    'exploration.loadingChecking': 'Checking destinations...',
    'exploration.loadingSearching': 'Searching tickets...',
  },
  es: {
    'app.name': 'PlaniAgent',
    'app.tagline': 'Tu agente de planificación de viajes',
    'nav.chat': 'Chat',
    'nav.exploration': 'Exploración',
    'nav.itineraries': 'Itinerarios',
    'nav.bookings': 'Reservas',
    'nav.saved': 'Guardados',
    'nav.buyCoffee': 'Invítame un café',
    'nav.contact': 'Contacto',
    'trip.current': 'Viaje Actual',
    'trip.edit': 'Editar',
    'trip.flights': 'Vuelos',
    'trip.hotels': 'Hoteles',
    'trip.pending': 'Pendiente',
    'trip.oneSelected': '1 Seleccionado',
    'trip.draftLabel': 'ITINERARIO BORRADOR',
    'trip.documents': 'Documentos del Viaje',
    'trip.uploadPassport': 'Subir Pasaporte',
    'contact.title': 'Contacto',
    'contact.namePlaceholder': 'Tu nombre',
    'contact.emailPlaceholder': 'Tu correo',
    'contact.messagePlaceholder': 'Preguntas, sugerencias o comentarios...',
    'contact.send': 'Enviar Mensaje',
    'contact.sending': 'Enviando...',
    'contact.success': '¡Gracias! Tu mensaje ha sido enviado.',
    'contact.error': 'Algo salió mal. Intenta de nuevo.',
    'chat.placeholder': 'Escribe tu solicitud de viaje...',
    'chat.today': 'Hoy',
    'chat.empty': 'Aún no hay mensajes. ¡Inicia una conversación!',
    'chat.devChatId': 'Ingresa ID de chat...',
    'hotel.intro': 'Aquí tienes algunas opciones de hotel:',
    'hotel.select': 'Seleccionar Este Hotel',
    'hotel.contactForPricing': 'Contactar por precio',
    'hotel.locationNotSpecified': 'Ubicación no especificada',
    'hotel.more': '+{n} más',
    'hotel.perNight': '/noche',
    'hotel.fallbackName': 'Hotel',
    'hotel.bookingPrefix': 'Me gustaría reservar:',
    'hotel.fallbackBookingName': 'este hotel',
    'hotel.currencySymbol': '$',
    'trip.sampleDates': '12-15 de junio',
    'trip.sampleLocation': 'Costa Amalfitana, Italia',
    'trip.draftHotelName': 'Hotel Santa Caterina',
    'trip.draftHotelLocation': 'Positano',
    'trip.draftRestaurantName': 'Cena en La Sponda',
    'trip.draftRestaurantDesc': 'Estrella Michelin',
    'item.activity': 'Actividad',
    'item.details': 'Detalles',
    'item.googleMaps': 'Google Maps',
    'item.ticket': 'Boleto',
    'item.bookOnGetYourGuide': 'Reservar en GetYourGuide',
    'item.day': 'Día',
    'item.recommendations': 'Recomendaciones',
    'share.exportPdf': 'Exportar como PDF',
    'share.pdfTitle': 'Itinerario PlaniAgent',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': 'Este itinerario fue generado en {website}',
    'share.openInMaps': 'Abrir en Google Maps',
    'share.shareItinerary': 'Compartir Itinerario',
    'share.linkCopied': 'Enlace copiado',
    'share.sharedItineraryMessage': 'Este es un itinerario compartido',
    'share.sharedItineraryTitle': 'Itinerario Compartido',
    'share.loadingItinerary': 'Cargando itinerario...',
    'nav.roteiro': 'Itinerario',
    'modal.fullScreen': 'Pantalla completa',
    'error.api': 'Error al obtener datos de la API: {message}',
    'bug.title': 'Reportar Error',
    'bug.selectCategory': 'Seleccionar categoría',
    'bug.categoryUI': 'Problema de UI',
    'bug.categoryFunctionality': 'Problema de Funcionalidad',
    'bug.categoryPerformance': 'Problema de Rendimiento',
    'bug.categoryContent': 'Problema de Contenido',
    'bug.categoryOther': 'Otro',
    'bug.descriptionPlaceholder': 'Describe el error que encontraste...',
    'bug.payloadLabel': 'Payload de Solicitud/Respuesta API',
    'bug.submit': 'Enviar Reporte de Error',
    'bug.sending': 'Enviando...',
    'bug.success': '¡Gracias! Tu reporte de error ha sido enviado.',
    'bug.error': 'Algo salió mal. Intenta de nuevo.',
    'bug.reportBug': 'Reportar Error',
    'greeting': '¡Hola! Soy tu agente de viajes personal. Puedo ayudarte con ~~vuelos~~, ~~hoteles~~ o itinerarios y recomendaciones. ¿A dónde vamos hoy?',
    'defaultMessage': 'Quiero un itinerario diario para un viaje a Río de Janeiro para una pareja del 5 al 8 de diciembre desde Dublín, con actividades variadas, llegada a las 2pm y salida a las 6pm',
    'exploration.badge': 'Destinos Destacados',
    'exploration.title': 'Explora Rutas por el Mundo',
    'exploration.subtitle': 'Selecciona una ruta para comenzar la planificación inmediata con tu asistente de viajes.',
    'exploration.startPlanning': 'Planificar esta Ruta',
    'exploration.filterRegion': 'Región',
    'exploration.filterDuration': 'Duración',
    'exploration.filterTravelStyle': 'Estilo de Viaje',
    'exploration.filterAll': 'Todas',
    'exploration.filterClear': 'Limpiar Filtros',
    'exploration.regionEurope': 'Europa',
    'exploration.regionBrazil': 'Brasil',
    'exploration.regionAmericas': 'Américas',
    'exploration.regionAsia': 'Asia',
    'exploration.regionAfrica': 'África',
    'exploration.styleRomantic': 'Romántica',
    'exploration.styleHistoric': 'Histórica',
    'exploration.styleBeach': 'Playa',
    'exploration.styleGastronomic': 'Gastronómica',
    'exploration.styleUrban': 'Urbana',
    'exploration.styleAdventure': 'Aventura',
    'exploration.styleCultural': 'Cultural',
    'exploration.styleNature': 'Naturaleza',
    'exploration.duration4Days': '4 días',
    'exploration.duration5Days': '5 días',
    'exploration.durationDays': '{days} días',
    'exploration.durationCustom': 'Personalizado',
    'exploration.loadingItinerary': 'Cargando itinerario...',
    'exploration.loadingConnecting': 'Conectando al agente...',
    'exploration.loadingRequesting': 'Solicitando itinerario...',
    'exploration.loadingChecking': 'Verificando destinos...',
    'exploration.loadingSearching': 'Buscando entradas...',
  },
  fr: {
    'app.name': 'PlaniAgent',
    'app.tagline': 'Votre agent de planification de voyage',
    'nav.chat': 'Discussion',
    'nav.exploration': 'Exploration',
    'nav.itineraries': 'Itinéraires',
    'nav.bookings': 'Réservations',
    'nav.saved': 'Enregistrés',
    'nav.buyCoffee': 'Offrez-moi un café',
    'nav.contact': 'Contact',
    'trip.current': 'Voyage Actuel',
    'trip.edit': 'Modifier',
    'trip.flights': 'Vols',
    'trip.hotels': 'Hôtels',
    'trip.pending': 'En attente',
    'trip.oneSelected': '1 Sélectionné',
    'trip.draftLabel': 'ITINÉRAIRE BROUILLON',
    'trip.documents': 'Documents de Voyage',
    'trip.uploadPassport': 'Télécharger le Passeport',
    'contact.title': 'Contact',
    'contact.namePlaceholder': 'Votre nom',
    'contact.emailPlaceholder': 'Votre email',
    'contact.messagePlaceholder': 'Questions, suggestions ou commentaires...',
    'contact.send': 'Envoyer le Message',
    'contact.sending': 'Envoi en cours...',
    'contact.success': 'Merci ! Votre message a été envoyé.',
    'contact.error': 'Une erreur est survenue. Veuillez réessayer.',
    'chat.placeholder': 'Saisissez votre demande de voyage...',
    'chat.today': 'Aujourd\'hui',
    'chat.empty': 'Aucun message pour le moment. Lancez une conversation !',
    'chat.devChatId': 'Entrez l\'ID de chat...',
    'hotel.intro': 'Voici quelques options d\'hôtels :',
    'hotel.select': 'Sélectionner Cet Hôtel',
    'hotel.contactForPricing': 'Contacter pour le prix',
    'hotel.locationNotSpecified': 'Emplacement non spécifié',
    'hotel.more': '+{n} autres',
    'hotel.perNight': '/nuit',
    'hotel.fallbackName': 'Hôtel',
    'hotel.bookingPrefix': 'Je souhaite réserver :',
    'hotel.fallbackBookingName': 'cet hôtel',
    'hotel.currencySymbol': '€',
    'trip.sampleDates': '12-15 juin',
    'trip.sampleLocation': 'Côte Amalfitaine, Italie',
    'trip.draftHotelName': 'Hotel Santa Caterina',
    'trip.draftHotelLocation': 'Positano',
    'trip.draftRestaurantName': 'Dîner La Sponda',
    'trip.draftRestaurantDesc': 'Étoilé Michelin',
    'item.activity': 'Activité',
    'item.details': 'Détails',
    'item.googleMaps': 'Google Maps',
    'item.ticket': 'Billet',
    'item.bookOnGetYourGuide': 'Réserver sur GetYourGuide',
    'item.day': 'Jour',
    'item.recommendations': 'Recommandations',
    'share.exportPdf': 'Exporter en PDF',
    'share.pdfTitle': 'Itinéraire PlaniAgent',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': 'Cet itinéraire a été généré sur {website}',
    'share.openInMaps': 'Ouvrir dans Google Maps',
    'share.shareItinerary': 'Partager l\'itinéraire',
    'share.linkCopied': 'Lien copié',
    'share.sharedItineraryMessage': 'Ceci est un itinéraire partagé',
    'share.sharedItineraryTitle': 'Itinéraire Partagé',
    'share.loadingItinerary': 'Chargement de l\'itinéraire...',
    'nav.roteiro': 'Itinéraire',
    'modal.fullScreen': 'Plein écran',
    'error.api': 'Échec de la récupération des données API : {message}',
    'bug.title': 'Signaler un Bug',
    'bug.selectCategory': 'Sélectionner une catégorie',
    'bug.categoryUI': 'Problème d\'interface',
    'bug.categoryFunctionality': 'Problème de fonctionnalité',
    'bug.categoryPerformance': 'Problème de performance',
    'bug.categoryContent': 'Problème de contenu',
    'bug.categoryOther': 'Autre',
    'bug.descriptionPlaceholder': 'Décrivez le bug que vous avez rencontré...',
    'bug.payloadLabel': 'Payload de Requête/Réponse API',
    'bug.submit': 'Envoyer le rapport de bug',
    'bug.sending': 'Envoi en cours...',
    'bug.success': 'Merci ! Votre rapport de bug a été envoyé.',
    'bug.error': 'Une erreur est survenue. Veuillez réessayer.',
    'bug.reportBug': 'Signaler un Bug',
    'greeting': 'Bonjour ! Je suis votre agent de voyage personnel. Je peux vous aider avec ~~des vols~~, ~~des hôtels~~ ou des itinéraires et recommandations. Où allons-nous aujourd\'hui ?',
    'defaultMessage': 'Je veux un itinéraire quotidien pour un voyage à Rio de Janeiro pour un couple du 5 au 8 décembre depuis Dublin, avec un mélange d\'activités, arrivée à 14h et départ à 18h',
    'exploration.badge': 'Destinations Phares',
    'exploration.title': 'Explorez les Itinéraires du Monde',
    'exploration.subtitle': 'Choisissez un itinéraire pour démarrer immédiatement la planification avec votre assistant de voyage.',
    'exploration.startPlanning': 'Planifier cet Itinéraire',
    'exploration.filterRegion': 'Région',
    'exploration.filterDuration': 'Durée',
    'exploration.filterTravelStyle': 'Style de Voyage',
    'exploration.filterAll': 'Toutes',
    'exploration.filterClear': 'Effacer les Filtres',
    'exploration.regionEurope': 'Europe',
    'exploration.regionBrazil': 'Brésil',
    'exploration.regionAmericas': 'Amériques',
    'exploration.regionAsia': 'Asie',
    'exploration.regionAfrica': 'Afrique',
    'exploration.styleRomantic': 'Romantique',
    'exploration.styleHistoric': 'Historique',
    'exploration.styleBeach': 'Plage',
    'exploration.styleGastronomic': 'Gastronomique',
    'exploration.styleUrban': 'Urbain',
    'exploration.styleAdventure': 'Aventure',
    'exploration.styleCultural': 'Culturel',
    'exploration.styleNature': 'Nature',
    'exploration.duration4Days': '4 jours',
    'exploration.duration5Days': '5 jours',
    'exploration.durationDays': '{days} jours',
    'exploration.durationCustom': 'Personnalisé',
    'exploration.loadingItinerary': 'Chargement de l\'itinéraire...',
    'exploration.loadingConnecting': 'Connexion à l\'agent...',
    'exploration.loadingRequesting': 'Demande d\'itinéraire...',
    'exploration.loadingChecking': 'Vérification des destinations...',
    'exploration.loadingSearching': 'Recherche de billets...',
  },
  zh: {
    'app.name': 'PlaniAgent',
    'app.tagline': '您的旅行规划助手',
    'nav.chat': '聊天',
    'nav.exploration': '探索',
    'nav.itineraries': '行程',
    'nav.bookings': '预订',
    'nav.saved': '收藏',
    'nav.buyCoffee': '请我喝咖啡',
    'nav.contact': '联系',
    'trip.current': '当前行程',
    'trip.edit': '编辑',
    'trip.flights': '航班',
    'trip.hotels': '酒店',
    'trip.pending': '待处理',
    'trip.oneSelected': '已选1个',
    'trip.draftLabel': '草稿行程',
    'trip.documents': '旅行文件',
    'trip.uploadPassport': '上传护照',
    'contact.title': '联系',
    'contact.namePlaceholder': '您的姓名',
    'contact.emailPlaceholder': '您的邮箱',
    'contact.messagePlaceholder': '问题、建议或反馈...',
    'contact.send': '发送消息',
    'contact.sending': '发送中...',
    'contact.success': '谢谢！您的消息已发送。',
    'contact.error': '出了点问题，请重试。',
    'chat.placeholder': '输入您的旅行请求...',
    'chat.today': '今天',
    'chat.empty': '暂无消息，开始对话吧！',
    'chat.devChatId': '输入聊天ID...',
    'hotel.intro': '以下是一些酒店选项：',
    'hotel.select': '选择此酒店',
    'hotel.contactForPricing': '联系询价',
    'hotel.locationNotSpecified': '未指定位置',
    'hotel.more': '+{n}更多',
    'hotel.perNight': '/晚',
    'hotel.fallbackName': '酒店',
    'hotel.bookingPrefix': '我想预订：',
    'hotel.fallbackBookingName': '此酒店',
    'hotel.currencySymbol': '¥',
    'trip.sampleDates': '6月12-15日',
    'trip.sampleLocation': '意大利阿马尔菲海岸',
    'trip.draftHotelName': '圣卡特琳娜酒店',
    'trip.draftHotelLocation': '波西塔诺',
    'trip.draftRestaurantName': 'La Sponda晚餐',
    'trip.draftRestaurantDesc': '米其林星级',
    'item.activity': '活动',
    'item.details': '详情',
    'item.googleMaps': '谷歌地图',
    'item.ticket': '门票',
    'item.bookOnGetYourGuide': '在GetYourGuide上预订',
    'item.day': '第',
    'item.recommendations': '推荐',
    'share.exportPdf': '导出为PDF',
    'share.pdfTitle': 'PlaniAgent 行程',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': '此行程由 {website} 生成',
    'share.openInMaps': '在谷歌地图中打开',
    'share.shareItinerary': '分享行程',
    'share.linkCopied': '链接已复制',
    'share.sharedItineraryMessage': '这是一个共享的行程',
    'share.sharedItineraryTitle': '共享行程',
    'share.loadingItinerary': '加载行程中...',
    'nav.roteiro': '行程',
    'modal.fullScreen': '全屏',
    'error.api': '从API获取数据失败：{message}',
    'bug.title': '报告错误',
    'bug.selectCategory': '选择类别',
    'bug.categoryUI': '界面问题',
    'bug.categoryFunctionality': '功能问题',
    'bug.categoryPerformance': '性能问题',
    'bug.categoryContent': '内容问题',
    'bug.categoryOther': '其他',
    'bug.descriptionPlaceholder': '描述您遇到的错误...',
    'bug.payloadLabel': 'API 请求/响应负载',
    'bug.submit': '提交错误报告',
    'bug.sending': '发送中...',
    'bug.success': '谢谢！您的错误报告已发送。',
    'bug.error': '出了点问题，请重试。',
    'bug.reportBug': '报告错误',
    'greeting': '您好！我是您的个人旅行助手。我可以帮您处理~~机票~~、~~酒店~~或行程和推荐。今天我们去哪里？',
    'defaultMessage': '我想要一份里约热内卢的每日行程，一对情侣，12月5日至8日从都柏林出发，混合不同活动，下午2点到达，下午6点离开',
    'exploration.badge': '热门目的地',
    'exploration.title': '探索世界各地的行程',
    'exploration.subtitle': '选择行程，立即与您的旅行助手开始制定计划。',
    'exploration.startPlanning': '规划此行程',
    'exploration.filterRegion': '地区',
    'exploration.filterDuration': '时长',
    'exploration.filterTravelStyle': '旅行风格',
    'exploration.filterAll': '全部',
    'exploration.filterClear': '清除筛选',
    'exploration.regionEurope': '欧洲',
    'exploration.regionBrazil': '巴西',
    'exploration.regionAmericas': '美洲',
    'exploration.regionAsia': '亚洲',
    'exploration.regionAfrica': '非洲',
    'exploration.styleRomantic': '浪漫',
    'exploration.styleHistoric': '历史',
    'exploration.styleBeach': '海滩',
    'exploration.styleGastronomic': '美食',
    'exploration.styleUrban': '城市',
    'exploration.styleAdventure': '冒险',
    'exploration.styleCultural': '文化',
    'exploration.styleNature': '自然',
    'exploration.duration4Days': '4天',
    'exploration.duration5Days': '5天',
    'exploration.durationDays': '{days}天',
    'exploration.durationCustom': '定制',
    'exploration.loadingItinerary': '加载行程中...',
    'exploration.loadingConnecting': '连接代理中...',
    'exploration.loadingRequesting': '请求行程中...',
    'exploration.loadingChecking': '检查目的地中...',
    'exploration.loadingSearching': '搜索门票中...',
  },
  hi: {
    'app.name': 'PlaniAgent',
    'app.tagline': 'आपका यात्रा योजना एजेंट',
    'nav.chat': 'चैट',
    'nav.exploration': 'खोज',
    'nav.itineraries': 'यात्रा कार्यक्रम',
    'nav.bookings': 'बुकिंग',
    'nav.saved': 'सहेजे गए',
    'nav.buyCoffee': 'मुझे कॉफ़ी पिलाएं',
    'nav.contact': 'संपर्क',
    'trip.current': 'वर्तमान यात्रा',
    'trip.edit': 'संपादित करें',
    'trip.flights': 'उड़ानें',
    'trip.hotels': 'होटल',
    'trip.pending': 'लंबित',
    'trip.oneSelected': '1 चयनित',
    'trip.draftLabel': 'मसौदा यात्रा कार्यक्रम',
    'trip.documents': 'यात्रा दस्तावेज़',
    'trip.uploadPassport': 'पासपोर्ट अपलोड करें',
    'contact.title': 'संपर्क',
    'contact.namePlaceholder': 'आपका नाम',
    'contact.emailPlaceholder': 'आपका ईमेल',
    'contact.messagePlaceholder': 'प्रश्न, सुझाव या प्रतिक्रिया...',
    'contact.send': 'संदेश भेजें',
    'contact.sending': 'भेज रहा है...',
    'contact.success': 'धन्यवाद! आपका संदेश भेज दिया गया है।',
    'contact.error': 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    'chat.placeholder': 'अपना यात्रा अनुरोध दर्ज करें...',
    'chat.today': 'आज',
    'chat.empty': 'अभी तक कोई संदेश नहीं। बातचीत शुरू करें!',
    'chat.devChatId': 'चैट आईडी दर्ज करें...',
    'hotel.intro': 'यहाँ कुछ होटल विकल्प हैं:',
    'hotel.select': 'यह होटल चुनें',
    'hotel.contactForPricing': 'मूल्य के लिए संपर्क करें',
    'hotel.locationNotSpecified': 'स्थान निर्दिष्ट नहीं',
    'hotel.more': '+{n} और',
    'hotel.perNight': '/रात',
    'hotel.fallbackName': 'होटल',
    'hotel.bookingPrefix': 'मैं बुक करना चाहता हूँ:',
    'hotel.fallbackBookingName': 'यह होटल',
    'hotel.currencySymbol': '₹',
    'trip.sampleDates': '12-15 जून',
    'trip.sampleLocation': 'अमाल्फी तट, इटली',
    'trip.draftHotelName': 'होटल सांता कैटरीना',
    'trip.draftHotelLocation': 'पॉज़िटानो',
    'trip.draftRestaurantName': 'ला स्पोंडा डिनर',
    'trip.draftRestaurantDesc': 'मिशेलिन स्टार',
    'item.activity': 'गतिविधि',
    'item.details': 'विवरण',
    'item.googleMaps': 'Google Maps',
    'item.ticket': 'टिकट',
    'item.bookOnGetYourGuide': 'GetYourGuide पर बुक करें',
    'item.day': 'दिन',
    'item.recommendations': 'सिफारिशें',
    'share.exportPdf': 'PDF के रूप में निर्यात करें',
    'share.pdfTitle': 'PlaniAgent यात्रा कार्यक्रम',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': 'यह यात्रा कार्यक्रम {website} पर तैयार किया गया',
    'share.openInMaps': 'Google Maps में खोलें',
    'share.shareItinerary': 'यात्रा कार्यक्रम साझा करें',
    'share.linkCopied': 'लिंक कॉपी हो गया',
    'share.sharedItineraryMessage': 'यह एक साझा किया गया यात्रा कार्यक्रम है',
    'share.sharedItineraryTitle': 'साझा किया गया यात्रा कार्यक्रम',
    'share.loadingItinerary': 'यात्रा कार्यक्रम लोड हो रहा है...',
    'nav.roteiro': 'यात्रा कार्यक्रम',
    'modal.fullScreen': 'पूर्ण स्क्रीन',
    'error.api': 'API से डेटा प्राप्त करने में विफल: {message}',
    'bug.title': 'बग रिपोर्ट करें',
    'bug.selectCategory': 'श्रेणी चुनें',
    'bug.categoryUI': 'UI समस्या',
    'bug.categoryFunctionality': 'कार्यक्षमता समस्या',
    'bug.categoryPerformance': 'प्रदर्शन समस्या',
    'bug.categoryContent': 'सामग्री समस्या',
    'bug.categoryOther': 'अन्य',
    'bug.descriptionPlaceholder': 'आपके सामने आई समस्या का वर्णन करें...',
    'bug.payloadLabel': 'API अनुरोध/प्रतिक्रिया पेलोड',
    'bug.submit': 'बग रिपोर्ट भेजें',
    'bug.sending': 'भेज रहा है...',
    'bug.success': 'धन्यवाद! आपकी बग रिपोर्ट भेज दी गई है।',
    'bug.error': 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    'bug.reportBug': 'बग रिपोर्ट करें',
    'greeting': 'नमस्ते! मैं आपका व्यक्तिगत यात्रा एजेंट हूँ। मैं ~~उड़ानें~~, ~~होटल~~ या यात्रा कार्यक्रम और सिफारिशों में मदद कर सकता हूँ। आज हम कहाँ जा रहे हैं?',
    'defaultMessage': 'मैं रियो डी जनेरियो की यात्रा के लिए एक दैनिक कार्यक्रम चाहता हूँ, एक जोड़े के लिए 5 दिसंबर से 8 दिसंबर तक डबलिन से, विभिन्न गतिविधियों के साथ, दोपहर 2 बजे आगमन और शाम 6 बजे प्रस्थान',
    'exploration.badge': 'विशेष गंतव्य',
    'exploration.title': 'दुनिया भर के यात्रा कार्यक्रम खोजें',
    'exploration.subtitle': 'अपने AI यात्रा एजेंट के साथ तत्काल योजना शुरू करने के लिए एक यात्रा कार्यक्रम चुनें।',
    'exploration.startPlanning': 'इस यात्रा कार्यक्रम की योजना बनाएं',
    'exploration.filterRegion': 'क्षेत्र',
    'exploration.filterDuration': 'अवधि',
    'exploration.filterTravelStyle': 'यात्रा शैली',
    'exploration.filterAll': 'सभी',
    'exploration.filterClear': 'फ़िल्टर साफ़ करें',
    'exploration.regionEurope': 'यूरोप',
    'exploration.regionBrazil': 'ब्राज़ील',
    'exploration.regionAmericas': 'अमेरिका',
    'exploration.regionAsia': 'एशिया',
    'exploration.regionAfrica': 'अफ़्रीका',
    'exploration.styleRomantic': 'रोमांटिक',
    'exploration.styleHistoric': 'ऐतिहासिक',
    'exploration.styleBeach': 'समुद्र तट',
    'exploration.styleGastronomic': 'खानपान',
    'exploration.styleUrban': 'शहरी',
    'exploration.styleAdventure': 'साहसिक',
    'exploration.styleCultural': 'सांस्कृतिक',
    'exploration.styleNature': 'प्रकृति',
    'exploration.duration4Days': '4 दिन',
    'exploration.duration5Days': '5 दिन',
    'exploration.durationDays': '{days} दिन',
    'exploration.durationCustom': 'कस्टम',
    'exploration.loadingItinerary': 'यात्रा कार्यक्रम लोड हो रहा है...',
    'exploration.loadingConnecting': 'एजेंट से कनेक्ट हो रहा है...',
    'exploration.loadingRequesting': 'यात्रा कार्यक्रम का अनुरोध कर रहा है...',
    'exploration.loadingChecking': 'गंतव्यों की जांच कर रहा है...',
    'exploration.loadingSearching': 'टिकटें खोज रहा है...',
  },
  'pt-BR': {
    'app.name': 'PlaniAgent',
    'app.tagline': 'Seu agente de planejamento de viagem',
    'nav.chat': 'Chat',
    'nav.exploration': 'Exploração',
    'nav.itineraries': 'Roteiros',
    'nav.bookings': 'Reservas',
    'nav.saved': 'Salvos',
    'nav.buyCoffee': 'Me pague um café',
    'nav.contact': 'Contato',
    'trip.current': 'Viagem Atual',
    'trip.edit': 'Editar',
    'trip.flights': 'Voos',
    'trip.hotels': 'Hotéis',
    'trip.pending': 'Pendente',
    'trip.oneSelected': '1 Selecionado',
    'trip.draftLabel': 'ROTEIRO RASCUNHO',
    'trip.documents': 'Documentos da Viagem',
    'trip.uploadPassport': 'Enviar Passaporte',
    'contact.title': 'Contato',
    'contact.namePlaceholder': 'Seu nome',
    'contact.emailPlaceholder': 'Seu email',
    'contact.messagePlaceholder': 'Perguntas, sugestões ou feedback...',
    'contact.send': 'Enviar Mensagem',
    'contact.sending': 'Enviando...',
    'contact.success': 'Obrigado! Sua mensagem foi enviada.',
    'contact.error': 'Algo deu errado. Tente novamente.',
    'chat.placeholder': 'Digite sua solicitação de viagem...',
    'chat.today': 'Hoje',
    'chat.empty': 'Nenhuma mensagem ainda. Inicie uma conversa!',
    'chat.devChatId': 'Digite o ID do chat...',
    'hotel.intro': 'Aqui estão algumas opções de hotéis:',
    'hotel.select': 'Selecionar Este Hotel',
    'hotel.contactForPricing': 'Contatar para preço',
    'hotel.locationNotSpecified': 'Local não especificado',
    'hotel.more': '+{n} mais',
    'hotel.perNight': '/noite',
    'hotel.fallbackName': 'Hotel',
    'hotel.bookingPrefix': 'Gostaria de reservar:',
    'hotel.fallbackBookingName': 'este hotel',
    'hotel.currencySymbol': 'R$',
    'trip.sampleDates': '12 a 15 de junho',
    'trip.sampleLocation': 'Costa Amalfitana, Itália',
    'trip.draftHotelName': 'Hotel Santa Caterina',
    'trip.draftHotelLocation': 'Positano',
    'trip.draftRestaurantName': 'Jantar no La Sponda',
    'trip.draftRestaurantDesc': 'Estrela Michelin',
    'item.activity': 'Atividade',
    'item.details': 'Detalhes',
    'item.googleMaps': 'Google Maps',
    'item.ticket': 'Ingresso',
    'item.bookOnGetYourGuide': 'Reservar no GetYourGuide',
    'item.day': 'Dia',
    'item.recommendations': 'Recomendações',
    'share.exportPdf': 'Exportar como PDF',
    'share.pdfTitle': 'Roteiro PlaniAgent',
    'share.pdfFooter': 'PlaniAgent',
    'share.generatedAt': 'Este roteiro foi gerado em {website}',
    'share.openInMaps': 'Abrir no Google Maps',
    'share.shareItinerary': 'Compartilhar Roteiro',
    'share.linkCopied': 'O link foi copiado',
    'share.sharedItineraryMessage': 'Este é um roteiro compartilhado',
    'share.sharedItineraryTitle': 'Roteiro Compartilhado',
    'share.loadingItinerary': 'Carregando roteiro...',
    'nav.roteiro': 'Roteiro',
    'modal.fullScreen': 'Tela cheia',
    'error.api': 'Falha ao buscar dados da API: {message}',
    'bug.title': 'Reportar Bug',
    'bug.selectCategory': 'Selecione a categoria',
    'bug.categoryUI': 'Problema de Interface',
    'bug.categoryFunctionality': 'Problema de Funcionalidade',
    'bug.categoryPerformance': 'Problema de Performance',
    'bug.categoryContent': 'Problema de Conteúdo',
    'bug.categoryOther': 'Outro',
    'bug.descriptionPlaceholder': 'Descreva o bug que você encontrou...',
    'bug.payloadLabel': 'Payload de Requisição/Resposta da API',
    'bug.submit': 'Enviar Relatório de Bug',
    'bug.sending': 'Enviando...',
    'bug.success': 'Obrigado! Seu relatório de bug foi enviado.',
    'bug.error': 'Algo deu errado. Tente novamente.',
    'bug.reportBug': 'Reportar Bug',
    'greeting': 'Olá! Sou seu agente de viagens pessoal. Posso ajudar com ~~voos~~, ~~hotéis~~ ou roteiros e recomendações. Para onde vamos hoje?',
    'defaultMessage': 'Quero um roteiro diário completo de 4 dias no Rio de Janeiro para um casal saindo de Dublin em ritmo moderado, em um pacote de viagem com mistura de diferentes atividades, chegando às 14h e saindo às 18h no último dia',
    'exploration.badge': 'Destinos em Destaque',
    'exploration.title': 'Explore Roteiros pelo Mundo',
    'exploration.subtitle': 'Selecione um roteiro para iniciar o planejamento imediato com seu assistente de viagem.',
    'exploration.startPlanning': 'Planejar este Roteiro',
    'exploration.filterRegion': 'Região',
    'exploration.filterDuration': 'Duração',
    'exploration.filterTravelStyle': 'Estilo de Viagem',
    'exploration.filterAll': 'Todas',
    'exploration.filterClear': 'Limpar Filtros',
    'exploration.regionEurope': 'Europa',
    'exploration.regionBrazil': 'Brasil',
    'exploration.regionAmericas': 'Américas',
    'exploration.regionAsia': 'Ásia',
    'exploration.regionAfrica': 'África',
    'exploration.styleRomantic': 'Romântica',
    'exploration.styleHistoric': 'Histórica',
    'exploration.styleBeach': 'Praia',
    'exploration.styleGastronomic': 'Gastronômica',
    'exploration.styleUrban': 'Urbana',
    'exploration.styleAdventure': 'Aventura',
    'exploration.styleCultural': 'Cultural',
    'exploration.styleNature': 'Natureza',
    'exploration.duration4Days': '4 dias',
    'exploration.duration5Days': '5 dias',
    'exploration.durationDays': '{days} dias',
    'exploration.durationCustom': 'Personalizado',
    'exploration.loadingItinerary': 'Carregando roteiro...',
    'exploration.loadingConnecting': 'Conectando ao agente...',
    'exploration.loadingRequesting': 'Solicitando roteiro...',
    'exploration.loadingChecking': 'Verificando destinos...',
    'exploration.loadingSearching': 'Buscando ingressos...',
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => detectBrowserLang());

  useEffect(() => {
    const handler = () => {
      const detected = detectBrowserLang();
      setLang(detected);
    };
    window.addEventListener('languagechange', handler);
    return () => window.removeEventListener('languagechange', handler);
  }, []);

  const t = (key, params = {}) => {
    const dict = translations[lang] || translations[FALLBACK_LANG];
    let text = dict[key];
    if (text === undefined) {
      text = translations[FALLBACK_LANG][key];
    }
    if (text === undefined) return key;
    if (params) {
      text = Object.entries(params).reduce((str, [k, v]) => str.replace(`{${k}}`, v), text);
    }
    return text;
  };

  const changeLang = (newLang) => {
    if (SUPPORTED_LANGS.includes(newLang)) {
      setLang(newLang);
    }
  };

  return (
    <I18nContext.Provider value={{ lang, t, changeLang, SUPPORTED_LANGS }}>
      {children}
    </I18nContext.Provider>
  );
}

const I18nContext = createContext(null);

export function useTranslation() {
  return useContext(I18nContext);
}

export default translations;
